import {PhoneHQSortHeader, PhoneHQIconAvatar, PhoneHQButton, PhoneHQInput, PhoneHQLoader, PhoneHQSwitch, StatusBadge, PhoneHQTabs, PhoneHQGlyph, PhoneHQCombobox} from 'ch3-ui-lib';
import {useState, useEffect} from "react";
import {mobileWidth, tabletWidth} from "../../utilis/screenWidthUtils.ts";
import {useSelector} from "react-redux";
import {useForm, Controller} from "react-hook-form";
import {Colors} from "../../utilis/types/Types";
import {
    useGetGoogleCalendarSettingsQuery,
    useGetUserProfileNotificationsQuery,
    useGetUserProfileQuery,
    usePatchGoogleCalendarSettingsMutation,
    useUpdateUserProfileMutation,
    useUpdateUserProfileNotificationsMutation,
    useUpdateUserTimezoneMutation,
} from "../../store/features/ProfileSettings/profileSettingsService";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {capitalize, isEqual} from "lodash";
import {timezoneList} from "../../utilis/timeUtilis.ts";
import {LocalTime} from "../../components/LocalTime.tsx";
import googleCalendar from "../../assets/google-calendar.svg";
import {GoogleStatus} from "./GoogleStatus.tsx";
import {EnsContactSection} from "./EnsContactSection.tsx";
import {AudioSettingsSection} from "./AudioSettingsSection";
import {audioSettingsMessages} from "./audioSettingsMessages";
import {useIntl} from "react-intl";
import listEmpty from '../../assets/no-groups.svg';
import {getFlag, getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";
import {FlagImage} from "react-international-phone";
import {useIsEnsEnabledQuery} from "../../services/chatService";

interface FormValues {
    firstName: string;
    lastName: string;
    email: string;
    position: string;
    role: string;
    missedCallEmailNotificationEnabled: boolean;
    userTimeZone: string;
}

const AUDIO_TAB_ID = '4';

const ProfileSettings = () => {
    const intl = useIntl();

    const [loading, setLoading] = useState(false)
    const [disabled, setDisabled] = useState(true);
    const [initialValues, setInitialValues] = useState<FormValues | null>(null);

    const [updateProfileSettings] = useUpdateUserProfileMutation();
    const [updateProfileNotifications] = useUpdateUserProfileNotificationsMutation();
    const [updateUserTimezone] = useUpdateUserTimezoneMutation();
    const [patchGoogleCalendarSettings] = usePatchGoogleCalendarSettingsMutation();

    const userId = useSelector(selectUserId);

    const {data: profileSettings, isLoading} = useGetUserProfileQuery(userId);
    const {data: profileNotifications} = useGetUserProfileNotificationsQuery(userId);
    const {data: personalizationInfoData} = useGetGoogleCalendarSettingsQuery(userId);
    const {data: isEnsEnabled} = useIsEnsEnabledQuery(undefined, {skip: !userId});
    useEffect(() => {
    }, [profileNotifications]);
    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: {errors},
    } = useForm<FormValues>({
        mode: 'onBlur',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            position: '',
            role: '',
            missedCallEmailNotificationEnabled: false,
            userTimeZone: ''
        },
    });

    const watchedValues = watch();

    const hasFormChanged = () => {
        if (!initialValues) return false;

        const currentValues = {
            firstName: getValues('firstName'),
            lastName: getValues('lastName'),
            position: getValues('position'),
            role: getValues('role'),
            missedCallEmailNotificationEnabled: getValues('missedCallEmailNotificationEnabled'),
            email: getValues('email'),
            userTimeZone: getValues('userTimeZone')
        };

        return !isEqual(initialValues, currentValues);
    };

    useEffect(() => {
        setDisabled(!hasFormChanged());
    }, [watchedValues, initialValues]);

    const handleToogleMissedCallEmailNotification = async (phoneNumberId: number, isEnabled: boolean) => {
        const disabledMissedCallEmailNotificationPhoneNumberIds = profileNotifications?.phoneNumberNotificationSettings
            .filter(element => !element.missedCallEmailNotificationEnabled)
            .map(el => el.phoneNumber.id) || []
        if (!isEnabled) {
            const index = disabledMissedCallEmailNotificationPhoneNumberIds.indexOf(phoneNumberId);
            if (index > -1) {
                disabledMissedCallEmailNotificationPhoneNumberIds.splice(index, 1);
            }
        } else {
            disabledMissedCallEmailNotificationPhoneNumberIds.push(phoneNumberId);
        }
        await updateProfileNotifications({
            userId: userId ?? '',
            disabledMissedCallEmailNotificationPhoneNumberIds: disabledMissedCallEmailNotificationPhoneNumberIds,
            missedCallEmailNotificationEnabled: profileNotifications?.missedCallEmailNotificationEnabled || false
        });
    }


    const generalInfo =
        <div
            className={`flex w-full flex-col px-8 py-4`}>
            <div className='flex w-full gap-8'>

                <div className='min-w-[240px] max-w-[240px] py-2'>
                    <div className='text-body-strong text-ink'>
                        General information
                    </div>
                    <div className='mt-1 text-footnote text-ink-muted'>
                        Update name and general information
                    </div>
                </div>
                <div
                    className={`grid w-full gap-4 rounded-card bg-surface p-4`}>

                    <div>
                        <Controller
                            name="firstName"
                            control={control}
                            rules={{required: 'First name is required'}}
                            render={({field}) => (
                                <PhoneHQInput
                                    phq-id='profileSettings-nameInput'
                                    {...field}
                                    value={field.value}
                                    onChange={({target: {value}}) => {
                                        field.onChange(value);
                                    }}
                                    error={errors.firstName ? errors.firstName.message : undefined}
                                    containerClassName='max-w-full' label='First name'
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Controller
                            name="lastName"
                            control={control}
                            rules={{required: 'Last name is required'}}
                            render={({field}) => (
                                <PhoneHQInput
                                    phq-id='profileSettings-lastNameInput'
                                    {...field}
                                    value={field.value}
                                    onChange={({target: {value}}) => {
                                        field.onChange(value);
                                    }}
                                    error={errors.lastName ? errors.lastName.message : undefined}
                                    containerClassName='max-w-full' label='Last name'
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Controller
                            name="email"
                            control={control}
                            rules={{required: 'email is required'}}
                            render={({field}) => (
                                <PhoneHQInput value={field.value}
                                         disabled
                                         phq-id='profileSettings-emailInput'
                                         onChange={({target: {value}}) => field.onChange(value)}
                                         containerClassName='max-w-full'
                                         label='Email adress'
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Controller
                            name="position"
                            control={control}
                            render={({field}) => (
                                <PhoneHQInput
                                    phq-id='profileSettings-positionInput'
                                    value={field.value}
                                    onChange={({target: {value}}) => {
                                        field.onChange(value);
                                    }}
                                    containerClassName='max-w-full  ' label='Position'/>
                            )}
                        />
                    </div>
                    <div>
                        <Controller
                            name="role"
                            control={control}
                            render={({field}) => (
                                <PhoneHQInput value={capitalize(field.value.toLowerCase())}
                                         disabled
                                         phq-id='profileSettings-roleInput'
                                         onChange={({target: {value}}) => field.onChange(value)}
                                         containerClassName='max-w-full capitalize' label='Role'/>
                            )}
                        />
                    </div>

                </div>
            </div>
            <div
                className={`flex w-full flex-col py-4`}>
                <div className='flex w-full gap-8'>

                    <div className='min-w-[240px] max-w-[240px] py-2'>
                        <div className='text-body-strong text-ink'>
                            Time
                        </div>

                    </div>

                    <div className={`grid w-full gap-4 rounded-card bg-surface p-4`}>
                        <label>
                            <div className=' font-semibold '>
                                Local time
                            </div>
                            <div className={'flex items-center gap-2'}>
                                <PhoneHQGlyph name='schedule' size={20} className='justify-start w-5'/>
                                <LocalTime timezone={watch('userTimeZone')}/>
                            </div>
                        </label>


                        <div>
                            <Controller
                                name="userTimeZone"
                                control={control}
                                render={({field}) => (
                                    <div>
                                        <PhoneHQCombobox
                                            label="Timezone"
                                            placeholder="Search timezone"
                                            value={field.value ?? profileSettings?.userTimeZone}
                                            onSelect={(item) => field.onChange(item.value)}
                                            items={timezoneList}/>
                                    </div>

                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {isEnsEnabled === true && <EnsContactSection/>}

        </div>

    const accessGroups =
        <div className='w-full  max-h-[calc(100vh-250px-var(--header-height))] overflow-y-auto '>
            <div className=' w-full '>
                {Boolean(profileSettings?.groups.length) &&
                    <div>
                        {profileSettings?.groups.map((group) => (
                            <div
                                className={`border-b `}>
                                <div className='flex gap-3 text-sm items-center pl-4 py-4'>
                                    <PhoneHQIconAvatar icon={group.icon} color={group.iconColor ?? Colors.auto} size="24px"/>{group.name}
                                </div>
                            </div>
                        ))}
                    </div>
                }
                {profileSettings?.groups.length === 0 &&
                    <div className={`flex text-center  justify-center `}>
                        <div className='w-[450px]  m-12'>
                            <div className='flex  justify-center w-full'>
                                <img className='m-4' src={listEmpty}/>
                            </div>

                            <p>
                                You are not assigned to any group
                            </p>


                        </div>

                    </div>
                }


            </div>
        </div>

    const accessNumbers =
        <div className='w-full max-h-[calc(100vh-250px-var(--header-height))] overflow-y-auto  '>
            <div className=' w-full '>
                {Boolean(profileSettings?.msisdns.length) &&
                    <div>
                        {profileSettings?.msisdns.map((number) => (
                            <div key={number.msisdnId}
                                 className={`border-b flex justify-between items-center `}>
                                <div className='flex gap-3 text-sm items-center pl-4 py-4'>
                                    <div>
                                        <PhoneHQIconAvatar icon={number.icon} color={number.iconColor} size="24px"/>
                                    </div>
                                    {number.label}
                                </div>
                                <div className='mr-4'>
                                    {number?.assigmentScope == 'PRIVATE' &&
                                        <StatusBadge tone="neutral">Private</StatusBadge>}
                                </div>

                            </div>
                        ))}
                    </div>
                }
                {profileSettings?.msisdns.length === 0 &&
                    <div className={`flex text-center  justify-center `}>
                        <div className='w-[450px]  m-12'>
                            <div className='flex  justify-center w-full'>
                                <img className='m-4' src={listEmpty}/>
                            </div>

                            <p>
                                You are not assigned to any phone number
                            </p>


                        </div>

                    </div>
                }

            </div>
        </div>

    const accessTabs = [
        {
            id: '1',
            label: 'Groups',
            content: accessGroups
        },
        {id: '2', label: 'Phone Numbers', content: accessNumbers}
    ];

    const accessInfo =
        <div className="flex gap-8 px-8 py-4">
            <div className='min-w-[240px] max-w-[240px] py-2'>
                <div className='text-body-strong text-ink'>
                    Your Access
                </div>
                <div className='mt-1 text-footnote text-ink-muted'>
                    You have access to the following groups and phone numbers.
                </div>
            </div>

            <div className="w-full">

                <PhoneHQTabs variant='underline' listClassName="px-2" className="h-full" contentClassName="h-full"
                        tabs={accessTabs}/>
            </div>

        </div>

    const notificationsInfo =
        <div className='flex w-full gap-8 px-8 py-4'>
            <div className='min-w-[240px] max-w-[240px] py-2'>
                <div className='text-body-strong text-ink'>
                    Missed call
                </div>

            </div>
            <div className="flex flex-col w-full">
                <div className={`w-full  p-6  flex  justify-between items-center `}>
                    <div className='text-ink-muted text-sm'>
                        Receive email notifications about missed external and direct internal calls.
                        Individual phone number switches below apply to external calls.
                    </div>
                    <div className=' flex justify-between '>
                        <Controller
                            name="missedCallEmailNotificationEnabled"
                            control={control}
                            render={({field}) => (
                                <PhoneHQSwitch checked={field.value}
                                        onCheckedChange={async () => {
                                            const disabledMissedCallEmailNotificationPhoneNumberIds = profileNotifications?.phoneNumberNotificationSettings
                                                .filter(element => !element.missedCallEmailNotificationEnabled)
                                                .map(el => el.phoneNumber.id) || []

                                            await updateProfileNotifications({
                                                userId: userId ?? '',
                                                missedCallEmailNotificationEnabled: !field.value,
                                                disabledMissedCallEmailNotificationPhoneNumberIds
                                            });
                                        }}/>
                            )}
                        />
                    </div>
                </div>
                <div className='w-full max-h-[calc(100vh-250px-var(--header-height))] overflow-y-auto  '>
                    <div className=' w-full '>
                        {Boolean(profileNotifications?.phoneNumberNotificationSettings?.length) &&
                            <table className="w-full">
                                <thead>
                                <tr className={`border-y `}>
                                    <th className='pl-6 py-3 w-full'
                                    ><PhoneHQSortHeader label={'Phone numbers'}/></th>

                                </tr>
                                </thead>
                                <tbody>
                                {profileNotifications?.phoneNumberNotificationSettings.map((number) => (
                                    <tr className="border-b">
                                        <td>
                                            <div
                                                className={` flex justify-between items-center `}>

                                                <div className={'flex gap-3 pl-6 py-2 text-sm items-center'}>
                                                    <div className='text-2xl items-end flex'>
                                                        <PhoneHQIconAvatar icon={number.phoneNumber.icon ? number.phoneNumber.icon : 'home'} color={profileNotifications?.missedCallEmailNotificationEnabled ? number.phoneNumber.iconColor : 'disabled'} size="24px"/>
                                                    </div>
                                                    <div
                                                        className={profileNotifications?.missedCallEmailNotificationEnabled ? '' : 'text-ink-muted'}>
                                                        <div className='text-sm'>
                                                            {number.phoneNumber.label}
                                                        </div>
                                                        <div className='flex items-center text-xs'>
                                                            <FlagImage
                                                                className={profileNotifications?.missedCallEmailNotificationEnabled ? ' ' : "grayscale"}
                                                                iso2={getFlag(number.phoneNumber.phoneNumber)}
                                                                size={16}/>
                                                            <div className={'ml-2'}>
                                                                {getFormatedPhoneNumber(number.phoneNumber.phoneNumber, true)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>


                                            </div>
                                        </td>
                                        <td className="pr-6 ">
                                            <PhoneHQSwitch
                                                    checked={number.missedCallEmailNotificationEnabled && watch('missedCallEmailNotificationEnabled')}
                                                    switchFirst
                                                    disabled={!watch('missedCallEmailNotificationEnabled')}
                                                    onCheckedChange={() =>
                                                        handleToogleMissedCallEmailNotification(number.phoneNumber.id, number.missedCallEmailNotificationEnabled)
                                                    }/>
                                        </td>
                                    </tr>


                                ))}
                                </tbody>
                            </table>
                        }

                    </div>


                </div>
            </div>
        </div>

    const personalizationInfo =
        <div className={'w-full flex flex-col'}>
            <div className="flex gap-8 px-8 py-4">
                <div className='min-w-[240px] max-w-[240px] py-2'>
                    <div className='text-body-strong text-ink'>
                        Calendars
                    </div>
                </div>
                <div className='w-full rounded-card bg-surface'>
                    <div className={`flex w-full gap-3 px-4 pt-4`}>
                        <img className={'w-8 h-8 my-2'} src={googleCalendar}/>

                        <div className="w-full pr-6 grid ">
                            <PhoneHQSwitch
                                checked={(personalizationInfoData?.googleCalendarSettingsResponse?.updateMeetingsStatus && personalizationInfoData?.googleCalendarSettingsResponse?.integrationActive) || false}
                                loading={loading}
                                disabled={!personalizationInfoData?.googleCalendarSettingsResponse?.integrationActive}
                                onCheckedChange={async () => {
                                    setLoading(true);
                                    await patchGoogleCalendarSettings({
                                        userId: Number(userId),
                                        updateMeetingsStatus: !personalizationInfoData?.googleCalendarSettingsResponse?.updateMeetingsStatus,
                                        updateDndOnMeetings: personalizationInfoData?.googleCalendarSettingsResponse?.updateDndOnMeetings
                                    });
                                    setLoading(false);
                                }}
                                label={'Sync status with Google Calendar'}
                                description={'Automatically update your availability status based on Google Calendar events'}
                            />
                        </div>
                    </div>
                    <div className={'px-4 pb-4 pl-[60px]'}>
                        <div className={'mt-2 text-footnote text-ink'}>
                            {profileSettings?.email}
                        </div>
                        <GoogleStatus
                            status={personalizationInfoData?.googleCalendarSettingsResponse?.integrationActive || false}/>
                    </div>
                </div>


            </div>
            {Boolean((personalizationInfoData?.googleCalendarSettingsResponse?.updateMeetingsStatus && personalizationInfoData?.googleCalendarSettingsResponse?.integrationActive)) &&
                <div className={'flex gap-8 px-8 py-4'}>
                    <div className='min-w-[240px] max-w-[240px] py-2'>
                        <div className='text-body-strong text-ink'>
                            Auto DND settings
                        </div>
                    </div>

                    <div className="grid w-full rounded-card bg-surface p-4">
                        <PhoneHQSwitch
                            checked={Boolean(personalizationInfoData?.googleCalendarSettingsResponse?.updateDndOnMeetings)}

                            onCheckedChange={() => {
                                patchGoogleCalendarSettings({
                                    userId: Number(userId),
                                    updateMeetingsStatus: personalizationInfoData?.googleCalendarSettingsResponse?.updateMeetingsStatus ?? false,
                                    updateDndOnMeetings: !personalizationInfoData?.googleCalendarSettingsResponse?.updateDndOnMeetings
                                });
                            }}
                            label={'Sync DND with calendar events'}
                            description={'When switched on, your \'Do Not Disturb\' mode follows your Google Calendar availability'}
                        />


                    </div>
                </div>}

        </div>


    const tabs = [
        {
            id: '0',
            label: 'Information',
            content: generalInfo
        },
        {id: '1', label: 'Access', content: accessInfo},
        {id: '2', label: 'Notifications', content: notificationsInfo},
        {id: '3', label: 'Personalization', content: personalizationInfo},
        {id: AUDIO_TAB_ID, label: intl.formatMessage(audioSettingsMessages.tab), content: <AudioSettingsSection/>}
    ];

    const [activeTabId, setActiveTabId] = useState(tabs[0].id);
    const handleTabChange = (tabId: string) => {
        setActiveTabId(tabId);
    };

    useEffect(() => {
        if (!profileSettings) return;

        const values = {
            firstName: profileSettings.firstName || '',
            lastName: profileSettings.lastName || '',
            email: profileSettings.email || '',
            position: profileSettings.position || '',
            role: profileSettings.role || '',
            userTimeZone: profileSettings.userTimeZone || '',
            setValue
        };

        setValue('firstName', values.firstName);
        setValue('lastName', values.lastName);
        setValue('email', values.email);
        setValue('position', values.position);
        setValue('role', values.role);
        setValue('userTimeZone', values.userTimeZone);

        setInitialValues({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            position: values.position,
            role: values.role,
            missedCallEmailNotificationEnabled: initialValues?.missedCallEmailNotificationEnabled || false,
            userTimeZone: values.userTimeZone
        });
    }, [profileSettings]);

    useEffect(() => {
        if (profileNotifications === undefined) return;

        const notificationValue = profileNotifications?.missedCallEmailNotificationEnabled || false;


        setValue('missedCallEmailNotificationEnabled', notificationValue);

        setInitialValues(prev => ({
            firstName: prev?.firstName || '',
            lastName: prev?.lastName || '',
            email: prev?.email || '',
            position: prev?.position || '',
            role: prev?.role || '',
            userTimeZone: prev?.userTimeZone || '',
            missedCallEmailNotificationEnabled: notificationValue,
        }));
    }, [profileNotifications]);

    const onSubmit = async () => {
        if (!profileSettings) return;
        const data = {
            userId: userId,
            firstName: getValues('firstName'),
            lastName: getValues('lastName'),
            position: getValues('position'),
            role: getValues('role'),
        };

        setLoading(true);
        await updateProfileSettings({data, userId});
        await updateUserTimezone({userTimeZone: getValues('userTimeZone'), userId: profileSettings.userId});
        setLoading(false);

        setInitialValues({
            firstName: getValues('firstName'),
            lastName: getValues('lastName'),
            position: getValues('position'),
            role: getValues('role'),
            email: getValues('email'),
            missedCallEmailNotificationEnabled: getValues('missedCallEmailNotificationEnabled'),
            userTimeZone: getValues('userTimeZone')

        });
        setDisabled(true);
    };

    if (isLoading) {
        return (
            <div className={`flex w-full`}>
                <PhoneHQLoader/>
            </div>
        );
    }

    return (
        profileSettings && <div className='w-full max-w-[1040px]'>
            <div className={`overflow-y-auto   ${mobileWidth || tabletWidth ? ' px-4' : ''}`}>
                <PhoneHQTabs
                    variant='pill'
                    className="h-full"
                    listClassName="mx-8 mb-2 mt-6"
                    contentClassName="h-full"
                    activeTab={activeTabId}
                    tabs={tabs}
                    onTabChange={handleTabChange}
                />
            </div>
            <div className={`flex justify-end px-8`}>
                <div className={`mx-8 mt-8 flex  justify-between`}>
                    <div className='flex gap-4'>
                        {(activeTabId !== AUDIO_TAB_ID) && ((tabs[Number(activeTabId)].label !== 'Notifications') && (tabs[Number(activeTabId)].label !== 'Personalization') && (tabs[Number(activeTabId)].label !== 'Access')) &&
                            <PhoneHQButton disabled={disabled} loading={loading} onClick={handleSubmit(onSubmit)} className='mb-8' phq-id='profileSetting-updateSettingsBtn'>Save changes</PhoneHQButton>}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProfileSettings;
